/**
 * Sleek AI Service
 * Provides AI-powered features using fast LLM inference
 * Features: Song search, Bible verse detection, Sermon summarization
 */

import { normalizeSummary } from '../lib/sermonSummary';
import { supabase } from './supabase';
import { findSong } from './songApi';

// AI requests go through /api/groq (a Vercel serverless function) so the
// Groq API key stays server-side. The function requires a signed-in user.
const GROQ_PROXY_URL = '/api/groq';

/**
 * Make a request to the Groq API via the server-side proxy
 */
async function groqRequest(messages, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('You must be signed in to use Sleek AI');
  }

  const response = await fetch(GROQ_PROXY_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages,
      temperature: options.temperature ?? 0.3,
      max_tokens: options.maxTokens ?? 2048
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Sleek AI error: ${response.status}`);
  }

  const data = await response.json();
  return data.choices[0]?.message?.content || '';
}

/**
 * Ask Groq to identify song title/artist candidates from noisy speech —
 * it is NOT trusted for lyric text (LLMs frequently misremember/hallucinate
 * exact lyrics). Real lyrics are always fetched afterward from actual
 * lyric sources (lrclib/Genius/ChartLyrics via songApi.js).
 */
async function identifySongCandidates(transcript, mode) {
  const prompt = mode === 'speak'
    ? `The user said this (possibly mis-heard by speech recognition) while looking for a worship song: "${transcript}"

Identify up to 3 likely worship songs they mean, correcting for speech-recognition errors (misheard words, missing punctuation).

Return ONLY a JSON array, no other text:
[{"title": "Exact Song Title", "artist": "Artist Name", "confidence": 0.95}]`
    : `The user sang or spoke these lyrics (possibly mis-heard by speech recognition): "${transcript}"

Identify up to 3 likely worship songs these lyrics belong to.

Return ONLY a JSON array, no other text:
[{"title": "Exact Song Title", "artist": "Artist Name", "confidence": 0.95}]`;

  const content = await groqRequest([
    { role: 'system', content: 'You are a Christian worship music expert. You identify song titles and artists only — you never generate or recall lyric text. Always respond with valid JSON arrays only.' },
    { role: 'user', content: prompt }
  ], { temperature: 0.2, maxTokens: 2048 });

  try {
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];
    const results = JSON.parse(jsonMatch[0]);
    return results
      .map(song => ({
        title: song.title || 'Unknown',
        artist: song.artist || '',
        confidence: song.confidence || 0.5
      }))
      .filter(song => song.title !== 'Unknown');
  } catch {
    console.error('Failed to parse Groq song identification results');
    return [];
  }
}

/**
 * Turn AI title/artist candidates into songs with real lyrics. A candidate is
 * only replaced by a lyric-source result when the title genuinely matches
 * (findSong returns null otherwise), so an unrelated song is never shown.
 */
async function resolveCandidates(candidates) {
  const resolved = await Promise.all(candidates.map(async (candidate) => {
    const match = await findSong(candidate.title, candidate.artist);
    if (match) {
      return { ...match, confidence: candidate.confidence, source: `Sleek AI + ${match.source}` };
    }
    return {
      title: candidate.title,
      artist: candidate.artist || 'Unknown',
      lyrics: 'Lyrics not available',
      confidence: candidate.confidence,
      source: 'Sleek AI (title only, no lyrics found)'
    };
  }));

  // The AI often lists one song by several artists; drop repeats of the same
  // resolved title + lyrics.
  const seen = new Set();
  return resolved.filter(song => {
    const key = `${song.title.toLowerCase()}|${song.lyrics.slice(0, 60)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * AI-powered song search
 * Takes spoken/sung text, identifies the likely song via Groq, then fetches
 * real lyrics for each candidate from actual lyric sources so the app never
 * displays hallucinated lyric text.
 */
export async function aiSongSearch(transcript, mode = 'speak') {
  const candidates = await identifySongCandidates(transcript, mode);
  if (candidates.length === 0) return [];

  return resolveCandidates(candidates);
}

/**
 * AI-powered song search by text query (for manual search box)
 * Uses Groq to resolve the query to real song titles/artists (handling
 * alternate names, misspellings, etc.), then fetches real lyrics for each.
 */
export async function aiManualSongSearch(query) {
  const content = await groqRequest([
    { role: 'system', content: 'You are a Christian worship music expert. You identify song titles and artists only — you never generate or recall lyric text. Always respond with valid JSON arrays only.' },
    { role: 'user', content: `Identify worship songs matching this search query: "${query}"

Consider title, artist, and common alternate names. Include songs from any Christian worship artist — Hillsong, Bethel, Elevation, Maverick City, Chris Tomlin, and others.

Return up to 5 results as a JSON array, ONLY the JSON array, no other text:
[{"title": "Exact Song Title", "artist": "Artist Name", "confidence": 0.95}]` }
  ], { temperature: 0.2, maxTokens: 2048 });

  let candidates = [];
  try {
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];
    candidates = JSON.parse(jsonMatch[0])
      .map(song => ({
        title: song.title || 'Unknown',
        artist: song.artist || '',
        confidence: song.confidence || 0.5
      }))
      .filter(song => song.title !== 'Unknown');
  } catch {
    console.error('Failed to parse Groq manual song search results');
    return [];
  }

  return resolveCandidates(candidates);
}

/**
 * AI-powered Bible verse detection
 * Takes sermon transcript text and identifies Bible references
 */
export async function aiBibleDetection(transcript) {
  const content = await groqRequest([
    { role: 'system', content: 'You are a Bible scholar. Extract Bible verse references from sermon transcripts. Always respond with valid JSON arrays only.' },
    { role: 'user', content: `Extract all Bible verse references from this sermon transcript. Include both explicit references (like "John 3:16") and implicit ones (like when someone quotes a verse without naming it).

Transcript: "${transcript}"

Return a JSON array with this format:
[{"reference": "John 3:16", "confidence": 0.95, "context": "brief explanation of why this reference was detected"}]

Return ONLY the JSON array, no other text. If no references found, return [].` }
  ], { temperature: 0.2, maxTokens: 2048 });

  try {
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];
    const results = JSON.parse(jsonMatch[0]);
    return results.map(ref => ({
      reference: ref.reference,
      confidence: ref.confidence || 0.5,
      context: ref.context || ''
    }));
  } catch {
    console.error('Failed to parse Groq Bible detection results');
    return [];
  }
}

/**
 * AI-powered sermon summarization
 * Takes accumulated sermon transcript and generates a summary
 */
export async function aiSermonSummary(transcript) {
  if (!transcript || transcript.trim().length < 50) {
    return normalizeSummary(null);
  }

  const content = await groqRequest([
    { role: 'system', content: 'You are a church ministry assistant that summarizes sermons. Always respond with valid JSON only.' },
    { role: 'user', content: `Summarize this sermon transcript. Provide a concise summary, key points, any Bible verses mentioned, discussion questions for a small group, and one practical takeaway for the week.

Transcript: "${transcript}"

Return JSON with this format:
{
  "summary": "2-3 sentence summary of the sermon",
  "keyPoints": ["Point 1", "Point 2", "Point 3"],
  "verses": ["John 3:16", "Romans 8:28"],
  "theme": "Main theme of the sermon",
  "discussionQuestions": ["Question 1", "Question 2", "Question 3"],
  "takeaway": "One practical thing to do this week"
}

Return ONLY the JSON object, no other text.` }
  ], { temperature: 0.4, maxTokens: 2048 });

  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return normalizeSummary(null);
    return normalizeSummary(JSON.parse(jsonMatch[0]));
  } catch {
    console.error('Failed to parse Groq sermon summary');
    return normalizeSummary(null);
  }
}

/**
 * Check if Sleek AI is available. The API key lives server-side now, so
 * availability just means Supabase (and therefore sign-in) is configured.
 */
export function isGroqAvailable() {
  return !!supabase;
}

const groqAiService = {
  aiSongSearch,
  aiManualSongSearch,
  aiBibleDetection,
  aiSermonSummary,
  isGroqAvailable
};

export default groqAiService;
